import { z } from 'zod';

/**
 * Comercialização por percentuais (corretagem + agenciamento) — compartilhado
 * entre PME (Vida em Grupo) e Capital Global.
 *
 * Os campos são strings mascaradas em pt-BR ("20,00"); a validação de negócio
 * vive nas funções puras abaixo — reaproveitadas pelos `validators` do form de
 * cada produto e testáveis à parte. Os tetos são parâmetros (com default) para
 * um produto poder divergir sem forkar a regra.
 */
export const commercializationPercentSchema = z.object({
  /** Percentual da corretagem, com máscara pt-BR ("20,00"). */
  brokeragePercent: z.string(),
  /** Percentual do agenciamento, com máscara pt-BR ("10,00"). */
  agencingPercent: z.string(),
});

export type CommercializationPercentValues = z.infer<
  typeof commercializationPercentSchema
>;

export const commercializationPercentDefaultValues: CommercializationPercentValues =
  {
    brokeragePercent: '',
    agencingPercent: '',
  };

/** Teto do carregamento comercial ("Fator Limite Comercial", 45%). */
export const COMMERCIAL_LOADING_LIMIT = 45;
/** O agenciamento entra diferido nas 12 parcelas do ano (mesmo tratamento do rating). */
export const AGENCY_FEE_INSTALLMENTS = 12;
/** Teto do agenciamento (%) — validado, NÃO travado no input (o campo nunca
 *  altera o valor digitado). */
export const AGENCY_PERCENT_MAX = 300;

/** "20,00" (máscara pt-BR) → 20; aceita milhar e "%" ("1.200,00%" → 1200). */
export function parsePercent(value: string): number {
  if (!value) return 0;
  return (
    Number(value.replace('%', '').trim().replace(/\./g, '').replace(',', '.')) ||
    0
  );
}

/** Máscara pt-BR do campo: consome dígitos e formata 2 casas. NÃO limita o
 *  valor — os tetos são validação, para não alterar o que foi digitado. */
/**
 * Máscara de percent SEM teto: digitar acima do limite mantém o que foi digitado
 * e a validação acusa o erro.
 *
 * NÃO reusa `formatPercentInputValue` de `@/shared/utils/format-percent.util` de
 * propósito — aquele CLAMPA (`return String(max)`), ou seja, reescreve o valor do
 * usuário ao passar do teto, que é exatamente o comportamento que não queremos aqui.
 */
export function formatPercentInput(raw: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  if (!digits) return '';
  const value = parseInt(digits, 10) / 100;
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Carga comercial = corretagem + agenciamento diferido em 12 parcelas. */
export function commercialLoading(brokerage: number, agency: number): number {
  return brokerage + agency / AGENCY_FEE_INSTALLMENTS;
}

/** A corretagem é obrigatória e nunca pode ser 0 (cobre vazio e "0,00"). */
export function validateBrokerage(
  brokeragePercent: string
): string | undefined {
  return parsePercent(brokeragePercent) > 0
    ? undefined
    : 'A corretagem é obrigatória e deve ser maior que 0.';
}

/** O agenciamento tem teto próprio — acima dele é erro (não trava o input). */
export function validateAgencing(
  agencingPercent: string,
  max: number = AGENCY_PERCENT_MAX
): string | undefined {
  return parsePercent(agencingPercent) > max
    ? `O agenciamento não pode ultrapassar ${max}%.`
    : undefined;
}

/** Corretagem + agenciamento/12 não pode ultrapassar o teto comercial. */
export function validateCommercialLimit(
  brokeragePercent: string,
  agencingPercent: string,
  limit: number = COMMERCIAL_LOADING_LIMIT
): string | undefined {
  const carga = commercialLoading(
    parsePercent(brokeragePercent),
    parsePercent(agencingPercent)
  );
  return carga > limit
    ? `A corretagem somada ao agenciamento não pode ultrapassar ${limit}%.`
    : undefined;
}
